import { useFlags } from "flagsmith/react";
import React, { PropsWithChildren, ReactElement } from "react";
import { Redirect } from "react-router-dom";

import { SIDEBAR_USER_FLAGS } from "@utils/featureFlags";

import ROUTES from "@config/routes";
import { useAuth } from "@context/auth/AuthContext";
import { Roles } from "@shared/model";
import {
  ROLE_CARRIER,
  ROLE_CIF_DRIVER,
} from "@components/Routes/TypesPermissions";
import { isLegacyProfileOnlyDriver } from "@utils/permissions/legacyPermissionChecks";
import { useSystemModulesAuthFlag } from "@hooks/useSystemModulesAuthFlag";

interface RouteGuardProps extends PropsWithChildren<{}> {
  children: ReactElement;
  rules?: (_: string) => boolean;
  flag?: string;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ children, rules, flag }) => {
  const { flag_for_access_management } = useFlags([
    "flag_for_access_management",
  ]);
  const useSystemModulesAuth = useSystemModulesAuthFlag();

  const flags = useFlags(SIDEBAR_USER_FLAGS);

  const { permissionSet } = useAuth();
  const isAdmin = permissionSet.ROLES.some(
    (role) => role === Roles.ADMIN || role === Roles.ADMINPORTAL
  );

  // Motorista (CIF) e Transportadora compartilham redirect para /agenda-e-vem
  const IS_DRIVER_OR_CARRIER_PROFILE = useSystemModulesAuth
    ? permissionSet.ROLES.includes(ROLE_CIF_DRIVER) ||
      permissionSet.ROLES.includes(ROLE_CARRIER)
    : isLegacyProfileOnlyDriver(permissionSet.SYSTEM_MODULES);

  const hasAccessProfileDriver = () => {
    return flag_for_access_management.enabled && IS_DRIVER_OR_CARRIER_PROFILE;
  };

  const hasAccess = () => {
    if (!flag_for_access_management.enabled) {
      return true;
    }

    const isAuthorized = permissionSet.SYSTEM_MODULES.some((authPermission) => {
      // rules=undefined é intencionalmente permissivo neste contexto.
      // No legado, rotas admin não possuíam RouteGuard algum — a proteção era
      // exclusivamente via ProfileRoute + adminMode. Com flag OFF, o comportamento
      // é idêntico ao anterior. Com flag ON, todas as rotas admin passam a exigir
      // módulos admin:* explícitos via getRouteGuardRules.
      const isRules = rules ? rules(authPermission) : true;
      const isFlag = Boolean(flag) ? flag && flags[flag].enabled : true;

      return isRules && isFlag;
    });

    return isAuthorized;
  };

  return isAdmin || hasAccess() ? (
    children
  ) : hasAccessProfileDriver() ? (
    <Redirect to={ROUTES.USER_ROUTES.ROADLOAD} />
  ) : (
    <Redirect to={ROUTES.USER_ROUTES.NOTFOUND} />
  );
};

export default RouteGuard;
